import { CreateEventButton, Loader } from 'components';
import GridScheduler from 'components/Grid/gridv2';
import { CREATE_EVENT_OVERALL } from 'constants/sidebars';
import { useLazyLoading } from 'hooks';
import React, {
  FC,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { initialFiltersState } from 'sidebars/CreateEventOverall/utils';
import {
  doGetCommunityTotal,
  doRequestOverallEvents,
} from 'store/actionCreators/currentCommunity';
import { openModal } from 'store/actionCreators/modal';
import {
  getCommunityTotal,
  getCurrentUserInCommunity,
} from 'store/selectors/currentCommunity';
import { getCurrentUserInGroup } from 'store/selectors/currentGroup';
import {
  getOverallEventsLoading,
  getOverallEventsTotalCount,
} from 'store/selectors/events';
import {
  Community,
  CommunityOverallScheduler,
  CommunityRoles,
  SchedulerFilter,
} from 'types';

import { useSchedulerPageData } from '../../../hooks/useSchedulerPageData';
import { SectionWrapper } from '../styled';
import { useGridData } from './hooks';
import { StyledStickyButtonBlock } from './styled';

export interface ScheduleProps {
  filters: SchedulerFilter;
  community: Community;
  usersFilter: string;
  overallScheduler?: CommunityOverallScheduler;
  zoom: number;
  headerHeight: number;
  filterValue?: string;
  setFilters?: (value: SchedulerFilter) => void;
}

const Schedule: FC<ScheduleProps> = ({
  filters,
  community,
  usersFilter,
  overallScheduler,
  zoom,
  headerHeight,
  filterValue,
  setFilters,
}) => {
  const { t } = useTranslation();
  const { communityId } = useParams();
  const dispatch = useDispatch();
  const eventsLoading = useSelector(getOverallEventsLoading);
  const eventsTotalCount = useSelector(getOverallEventsTotalCount);
  const currentUser = useSelector(getCurrentUserInCommunity);
  const currentUserInGroup = useSelector(getCurrentUserInGroup);
  const total = useSelector(getCommunityTotal);

  const topHeadHeight = useMemo(() => {
    return headerHeight;
  }, [headerHeight]);

  const [showNoDataFound, setShowNoDataFound] = useState(false);

  const [viewFromFilter, setViewFromFilter] = useState(filters.from);

  useEffect(() => {
    setViewFromFilter(filters.from);
  }, [filters.from]);

  const {
    eventsToRender,
    footerData,
    openGiveReplySidebar,
    openTaskDetailsSidebar,
    openViewMemberSidebar,
    onHeaderClick,
    onFooterClick,
  } = useSchedulerPageData({
    events: overallScheduler?.events,
    community,
    filters: { ...filters, from: viewFromFilter },
    total,
    showNoDataCard: showNoDataFound,
  });

  const canCreateEvent = [CommunityRoles.OWNER, CommunityRoles.ADMIN].includes(
    currentUser?.communityPermission?.role,
  );

  const gridData = useGridData({
    tasks: community?.eventTasks,
    eventsToRender,
    responses: community?.eventResponses,
    openGiveReplySidebar,
    openTaskDetailsSidebar,
    currentUser,
    currentUserInGroup,
    overallScheduler,
    zoom,
    openViewMemberSidebar,
    community,
  });

  const eventsFilterValues = useMemo(() => {
    return {
      communityId,
      from: filters.from,
      to: filters.to,
      eventTypeId: filters.eventType.value,
      filterValue: usersFilter,
    };
  }, [filters, usersFilter]);

  const loadEvents = (filterValues) => {
    dispatch(doRequestOverallEvents(filterValues));
  };

  useEffect(() => {
    if (communityId && overallScheduler?.events) {
      dispatch(
        doGetCommunityTotal({
          communityId,
          from: filters.from,
          to: filters.to,
        }),
      );
    }
  }, [communityId, usersFilter, filters]);

  const pageSize = 25;
  const pageSizePreview = 3;

  const { listRef }: { listRef: RefObject<HTMLElement> } = useLazyLoading({
    isLoading: eventsLoading,
    loader: loadEvents,
    pageSize,
    listLength: overallScheduler?.events?.length || 0,
    hasNextPage:
      overallScheduler?.events &&
      overallScheduler.events.length < eventsTotalCount,
    cleaner: () => {},
    filterValues: eventsFilterValues,
    scollOffset: 1000,
  });

  const openCreateEventSidebar = () => {
    dispatch(
      openModal({
        type: CREATE_EVENT_OVERALL,
        placement: 'right',
        data: {
          overallScheduler,
          filterFrom: filters.from,
          filterTo: initialFiltersState(5).to,
          currentViewFrom: filters.from,
          currentViewTo: filters.to,
        },
      }),
    );
  };

  const [isPrepending, setIsPrepending] = useState(false);
  const [oldEventsCount, setOldEventsCount] = useState(0);
  const [oldPage, setOldPage] = useState(1);

  useEffect(() => {
    if (overallScheduler?.noMorePastEvents) {
      setShowNoDataFound(true);
      const toggle = document.getElementById('toggle-filters-icon');
      if (toggle) {
        toggle.click();
        setTimeout(() => {
          const toggleRefreshed = document.getElementById(
            'toggle-filters-icon',
          );
          if (toggleRefreshed?.getAttribute('data-is-open') === 'true') {
            toggleRefreshed.click();
          }
        }, 3000);
      }
    }
  }, [overallScheduler?.noMorePastEvents]);

  useEffect(() => {
    setShowNoDataFound(false);
  }, [filters]);

  useEffect(() => {
    if (!eventsLoading && isPrepending) {
      const newTotal = overallScheduler?.events?.length || 0;
      const added = newTotal - oldEventsCount;
      const eventBoxWidth = Math.floor(157 * zoom);
      const startPosition = added * eventBoxWidth;
      const targetPosition = Math.max(0, added - 3) * eventBoxWidth;

      if (listRef.current) {
        listRef.current.scroll({
          left: startPosition,
          behavior: 'smooth',
        });

        setTimeout(() => {
          listRef.current.scroll({
            left: targetPosition,
            behavior: 'smooth',
          });
        }, 50);
      }
      setIsPrepending(false);
    }
  }, [
    eventsLoading,
    isPrepending,
    zoom,
    overallScheduler?.events?.length,
    oldEventsCount,
  ]);

  const fetchOlderEvents = useCallback(() => {
    if (eventsLoading || showNoDataFound) return;

    const limitDate = new Date();
    limitDate.setFullYear(limitDate.getFullYear() - 10);

    const newFrom = new Date(filters.from);
    newFrom.setFullYear(newFrom.getFullYear() - 5);
    const fetchToDate = new Date(filters.from);
    fetchToDate.setDate(fetchToDate.getDate() - 1);

    setOldPage(oldPage + 1);
    setOldEventsCount(overallScheduler?.events?.length || 0);

    if (oldPage === 1) {
      dispatch(
        doGetCommunityTotal({
          communityId,
          from: newFrom,
          to: fetchToDate,
          prepend: true,
        }),
      );
    }

    if (newFrom < viewFromFilter) {
      setViewFromFilter(newFrom);
    }

    dispatch(
      doRequestOverallEvents({
        ...eventsFilterValues,
        from: newFrom,
        to: fetchToDate,
        page: oldPage,
        limit: pageSizePreview,
        prepend: true,
        isOlderEvents: true,
      }),
    );
    setIsPrepending(true);
  }, [
    eventsLoading,
    overallScheduler?.events,
    showNoDataFound,
    communityId,
    dispatch,
    eventsFilterValues,
    pageSizePreview,
  ]);

  if (
    eventsLoading &&
    !overallScheduler?.events?.length &&
    overallScheduler?.noMorePastEvents === false
  ) {
    return <Loader />;
  }

  return (
    <SectionWrapper height={topHeadHeight}>
      <>
        {eventsLoading ? <Loader floating={true} /> : null}
        <GridScheduler
          filterValue={filterValue}
          listRef={listRef}
          tableId={communityId}
          data={gridData}
          footer={footerData}
          header={eventsToRender}
          topHeadHeight={topHeadHeight}
          zoom={zoom}
          onHeaderClick={onHeaderClick}
          onFooterClick={onFooterClick}
          onScrollStartReached={fetchOlderEvents}
          isLoading={eventsLoading}
          hasNextPage={
            overallScheduler?.events &&
            overallScheduler.events.length < eventsTotalCount
          }
          hasPreviousPage={!overallScheduler?.noMorePastEvents}
        />
      </>
      {canCreateEvent && (
        <StyledStickyButtonBlock>
          <CreateEventButton
            width="72px"
            onClick={openCreateEventSidebar}
            extraText={t('event.createEvent')}
          />
        </StyledStickyButtonBlock>
      )}
    </SectionWrapper>
  );
};

export default Schedule;
